import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, Building2, Users, User, Handshake } from "lucide-react";
import { getAppUrl } from "@/utils/domain";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export const PublicHeader = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  const navLinks = [
    { label: "Memorials", path: "/search" },
    { label: "About", path: "/about" },
    { label: "Contact", path: "/contact" },
  ];

  const isActive = (path: string) => location.pathname === path;

  const handleNavClick = () => setIsMobileMenuOpen(false);

  return (
    <header
      className="sticky top-0 z-50"
      style={{ backgroundColor: "#787559" }}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
        {/* Desktop: 3-column grid — nav | logo | login */}
        <div className="hidden lg:grid grid-cols-3 items-center h-14">
          {/* Left — navigation */}
          <nav className="flex items-center gap-6">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-sm font-medium transition-colors ${
                  isActive(link.path)
                    ? "text-white"
                    : "text-white/70 hover:text-white"
                }`}
              >
                {link.label}
              </Link>
            ))}

            {/* Partners dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger
                className="flex items-center gap-1 text-sm font-medium text-white/70 hover:text-white transition-colors focus:outline-none"
              >
                Partners
                <ChevronDown className="h-3.5 w-3.5" />
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="start"
                className="w-56"
                style={{ backgroundColor: "#1E1F18", border: "1px solid rgba(255,255,255,0.12)" }}
              >
                <DropdownMenuItem asChild>
                  <a
                    href={getAppUrl("/portal/dashboard")}
                    className="flex items-center cursor-pointer text-white/80 hover:text-white focus:text-white focus:bg-white/10"
                  >
                    <Building2 className="mr-3 h-4 w-4" />
                    Monument Company Portal
                  </a>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <a
                    href={getAppUrl("/partner/dashboard")}
                    className="flex items-center cursor-pointer text-white/80 hover:text-white focus:text-white focus:bg-white/10"
                  >
                    <Users className="mr-3 h-4 w-4" />
                    Fulfillment Partner Portal
                  </a>
                </DropdownMenuItem>
                <div className="my-1 border-t" style={{ borderColor: "rgba(255,255,255,0.12)" }} />
                <DropdownMenuItem asChild>
                  <Link
                    to="/partners"
                    className="flex items-center cursor-pointer text-white/80 hover:text-white focus:text-white focus:bg-white/10"
                  >
                    <Handshake className="mr-3 h-4 w-4" />
                    Become a Partner
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </nav>

          {/* Center — logo */}
          <div className="flex justify-center">
            <Link to="/" className="flex items-center gap-2">
              <img
                src="/lovable-uploads/pontis-logo.svg"
                alt="Pontis"
                className="h-7 w-auto brightness-0 invert"
              />
              <img
                src="/lovable-uploads/pontis-wordmark.png"
                alt="Pontis"
                className="h-4 w-auto brightness-0 invert"
              />
            </Link>
          </div>

          {/* Right — login button */}
          <div className="flex justify-end">
            <a
              href={getAppUrl("/auth")}
              className="inline-flex items-center gap-2 px-5 py-1.5 rounded-full text-sm font-medium transition-colors"
              style={{
                backgroundColor: "#FFFEF5",
                color: "#787559",
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLAnchorElement).style.backgroundColor = "#F0EEE4";
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLAnchorElement).style.backgroundColor = "#FFFEF5";
              }}
            >
              Login
            </a>
          </div>
        </div>

        {/* Mobile: hamburger | logo | spacer */}
        <div className="lg:hidden grid grid-cols-3 items-center h-14">
          <div className="justify-self-start">
            <button
              className="p-2 text-white/70 hover:text-white"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? (
                <X className="h-5 w-5" />
              ) : (
                <Menu className="h-5 w-5" />
              )}
            </button>
          </div>

          <Link to="/" className="justify-self-center flex items-center gap-2">
            <img
              src="/lovable-uploads/pontis-logo.svg"
              alt="Pontis"
              className="h-6 w-auto brightness-0 invert"
            />
            <img
              src="/lovable-uploads/pontis-wordmark.png"
              alt="Pontis"
              className="h-3.5 w-auto brightness-0 invert"
            />
          </Link>

          <div className="justify-self-end">
            <a
              href={getAppUrl("/auth")}
              className="text-xs font-medium px-3 py-1 rounded-full"
              style={{
                backgroundColor: "#FFFEF5",
                color: "#787559",
              }}
            >
              Login
            </a>
          </div>
        </div>

        {/* Mobile menu drawer */}
        {isMobileMenuOpen && (
          <div
            className="lg:hidden py-4 border-t"
            style={{ borderColor: "rgba(255,255,255,0.08)" }}
          >
            <nav className="flex flex-col gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className="px-3 py-2.5 text-sm font-medium text-white/70 hover:text-white rounded-md hover:bg-white/5 transition-colors"
                  onClick={handleNavClick}
                >
                  {link.label}
                </Link>
              ))}
              <div
                className="mt-2 pt-3 border-t"
                style={{ borderColor: "rgba(255,255,255,0.08)" }}
              >
                <p
                  className="px-3 text-xs uppercase tracking-wide mb-2 font-medium"
                  style={{ color: "rgba(255,255,255,0.35)" }}
                >
                  Partners
                </p>
                <a
                  href={getAppUrl("/portal/dashboard")}
                  className="flex items-center px-3 py-2.5 text-sm text-white/70 hover:text-white rounded-md hover:bg-white/5"
                  onClick={handleNavClick}
                >
                  <Building2 className="mr-3 h-4 w-4" />
                  Monument Company Portal
                </a>
                <a
                  href={getAppUrl("/partner/dashboard")}
                  className="flex items-center px-3 py-2.5 text-sm text-white/70 hover:text-white rounded-md hover:bg-white/5"
                  onClick={handleNavClick}
                >
                  <Users className="mr-3 h-4 w-4" />
                  Fulfillment Partner Portal
                </a>
                <div className="my-1 border-t" style={{ borderColor: "rgba(255,255,255,0.08)" }} />
                <Link
                  to="/partners"
                  className="flex items-center px-3 py-2.5 text-sm text-white/70 hover:text-white rounded-md hover:bg-white/5"
                  onClick={handleNavClick}
                >
                  <Handshake className="mr-3 h-4 w-4" />
                  Become a Partner
                </Link>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};
